import {
  AccessGrant,
  GrantReason,
  OrgWideReason,
  ResolvedBuilding,
  ScopeType,
} from '../../../../interfaces/hierarchy-interfaces';
import { PillTone } from './dashboard-scopes.styled';

// The words this screen uses for access, in one place.
//
// Scope management is the part of the product where a wrong mental model is
// expensive: an admin who reads "organization" as "the buildings I can see
// today" will not understand why a manager kept working after a new building
// was bought — or, worse, will grant org-wide access believing it is narrow.
// Every string below is written to make the breadth of a scope unambiguous.

export const SCOPE_TYPE_LABELS: Record<ScopeType, string> = {
  organization: 'Organisation',
  portfolio: 'Portfolio',
  building: 'Building',
  unit: 'Unit',
};

// Shown under the scope-type picker. The organization and portfolio copy names
// the future-buildings behaviour explicitly, because that is the single fact
// that distinguishes these grants from a stack of building grants.
export const SCOPE_TYPE_DESCRIPTIONS: Record<ScopeType, string> = {
  organization:
    'Every building in the organisation — including any building added later.',
  portfolio:
    'Every building in one portfolio — including buildings added to that portfolio later.',
  building: 'One specific building. New buildings are not included.',
  unit: 'One specific unit inside a building. The narrowest grant there is.',
};

// WHEN SOMEBODY REACHES EVERY BUILDING WITHOUT A GRANT SAYING SO.
//
// The buildings column counts rows in access_scope, and for these three cases
// that count is zero while the true answer is "all of them". Rendering the count
// alone is what put a "reaches no building" warning on the owner's own row.
//
// Each label names the mechanism, because the remedy differs: an Owner is
// working as designed, an organization grant is a deliberate act somebody can
// revoke, and enforcement being off is a SETTING that silently widens every
// person in the organisation and is the one an admin most needs to know about.
// The narrow list column gets the short form; the detail screen, which has the
// room, gets the qualified one. Both say "all", because that is the fact the
// count could not carry.
export const ORG_WIDE_SHORT = 'All buildings';

export const ORG_WIDE_LABELS: Record<OrgWideReason, string> = {
  owner: 'All buildings (Owner)',
  organization_grant: 'All buildings (organisation-wide grant)',
  enforcement_off: 'All buildings (scope enforcement is off)',
};

export const ORG_WIDE_EXPLANATIONS: Record<OrgWideReason, string> = {
  owner:
    'Owners reach every building in the organisation, including buildings added later. No grant is needed and none is shown.',
  organization_grant:
    'An organisation-wide grant reaches every building, including buildings added later.',
  enforcement_off:
    'Scope enforcement is switched off for this organisation, so every staff member currently reaches every building regardless of their grants.',
};

// Enforcement-off is the one an admin should act on: it is a setting, it applies
// to everybody, and it is almost never what someone assigning grants expects.
export const orgWideTone = (reason: OrgWideReason): PillTone =>
  reason === 'enforcement_off' ? 'warning' : 'brand';

// Organization reads as brand because it is the only scope whose reach grows on
// its own; the rest are neutral so the exception stays visible.
export const scopeTypeTone = (scopeType: ScopeType): PillTone =>
  scopeType === 'organization' ? 'brand' : 'neutral';

// A grant whose reach can expand without anyone touching it again.
export const isSelfExpandingScope = (scopeType: ScopeType): boolean =>
  scopeType === 'organization' || scopeType === 'portfolio';

export const GRANT_REASON_LABELS: Record<GrantReason, string> = {
  assignment: 'Assignment',
  coverage: 'Coverage',
  training: 'Training',
  temporary: 'Temporary',
};

// Plain-language capability copy. `can_administer` is described in terms of
// consequences rather than table names — "can hand access to someone else" is
// the part an admin needs to weigh.
export const CAPABILITY_COPY = {
  operate: {
    title: 'Day-to-day access',
    help: 'View residents and units, log visitors, run the front desk inside this scope. This is what most staff need.',
  },
  administer: {
    title: 'Administrative control',
    help: 'Change the structure inside this scope — add and edit buildings and units — and give other people access to it. Only an owner can grant this.',
  },
} as const;

// What the grant points at, in one line. Falls back to the id when the router
// could not resolve a name (the target row was deleted after the grant was
// written), so a dangling grant is still identifiable instead of blank.
export const grantTargetLabel = (grant: AccessGrant): string => {
  switch (grant.scope_type) {
    case 'organization':
      return 'Entire organisation';
    case 'portfolio':
      return grant.portfolio_name || `Portfolio #${grant.portfolio_id ?? '?'}`;
    case 'building':
      return grant.building_name || `Building #${grant.building_id ?? '?'}`;
    case 'unit':
      return grant.unit_number
        ? `Unit ${grant.unit_number}`
        : `Unit #${grant.unit_id ?? '?'}`;
    default:
      return 'Unknown scope';
  }
};

// ---------------------------------------------------------------------------
// Why does this person reach this building?
// ---------------------------------------------------------------------------

// The API returns grants and resolved buildings as two independent lists: it
// says WHAT a user can reach but not WHICH grant put it there. Rather than show
// an unexplained building list, this re-applies the same expansion rules the
// server's trigger uses, so every resolved building can name its source.
//
// This is a display-side derivation, not an authorization decision — the server
// remains the only thing that decides access. Its one job is to keep an admin
// from having to guess which grant to revoke to take a building away.
export interface AccessSource {
  grantId: string;
  label: string;
  tone: PillTone;
  scopeType: ScopeType;
}

// Only OPEN grants are expanded into access_scope, so a closed grant never
// explains a resolved building even when its target matches.
const grantCoversBuilding = (
  grant: AccessGrant,
  building: ResolvedBuilding
): boolean => {
  if (!grant.is_open) {
    return false;
  }

  switch (grant.scope_type) {
    case 'organization':
      return true;
    case 'portfolio':
      return (
        grant.portfolio_id !== null &&
        grant.portfolio_id === building.portfolio_id
      );
    case 'building':
      return grant.building_id === building.building_id;
    // A unit grant reaches the building only through the units it named, which
    // are exactly the ids collected into the resolved row.
    case 'unit':
      return (
        grant.unit_id !== null && building.unit_ids.includes(grant.unit_id)
      );
    default:
      return false;
  }
};

const sourceLabel = (grant: AccessGrant): string => {
  switch (grant.scope_type) {
    case 'organization':
      return 'Organisation-wide grant';
    case 'portfolio':
      return `Portfolio: ${grantTargetLabel(grant)}`;
    case 'building':
      return 'Direct building grant';
    case 'unit':
      return `Unit grant: ${grantTargetLabel(grant)}`;
    default:
      return 'Grant';
  }
};

// Every open grant that reaches this building. More than one is normal and
// worth showing: revoking a single grant will not remove access when a second
// one still covers the same building.
export const explainBuildingAccess = (
  building: ResolvedBuilding,
  grants: AccessGrant[]
): AccessSource[] =>
  grants
    .filter(grant => grantCoversBuilding(grant, building))
    .map(grant => ({
      grantId: grant.id,
      label: sourceLabel(grant),
      tone: scopeTypeTone(grant.scope_type),
      scopeType: grant.scope_type,
    }));

// How many buildings an open grant currently accounts for — the number that
// makes "one grant, twelve buildings" concrete on the grant card itself.
export const buildingsFromGrant = (
  grant: AccessGrant,
  resolvedBuildings: ResolvedBuilding[]
): number =>
  resolvedBuildings.filter(building => grantCoversBuilding(grant, building))
    .length;

// Whether revoking this grant would actually take anything away, or whether
// another open grant already covers everything it covers. Prevents the
// "I revoked it and nothing changed" surprise.
export const isFullyRedundant = (
  grant: AccessGrant,
  grants: AccessGrant[],
  resolvedBuildings: ResolvedBuilding[]
): boolean => {
  if (!grant.is_open) {
    return false;
  }

  const covered = resolvedBuildings.filter(building =>
    grantCoversBuilding(grant, building)
  );

  if (covered.length === 0) {
    return false;
  }

  const others = grants.filter(other => other.id !== grant.id);

  return covered.every(building =>
    others.some(other => grantCoversBuilding(other, building))
  );
};

export const getInitials = (name?: string | null): string => {
  if (!name) {
    return '?';
  }

  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return '?';
  }

  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';

  return (first + last).toUpperCase();
};

// name and email are AES-GCM columns that come back null when they cannot be
// decrypted, so neither is safe to render bare.
export const displayName = (
  name: string | null,
  email: string | null
): string => name || email || 'Unnamed staff member';
